import { injectable } from 'inversify';
import { ClientItem, FamilyRelationship } from '@shared/models/family';
import container from '@core/di';
import FamiliesService, {
  GetFamiliesParams,
  IAddFamilyData,
  IAddTcmData,
  IEditFamilyData,
  ITimezone,
} from '@shared/services/families';
import BaseStore from '@core/stores/base';
import { runInAction, makeObservable, observable, action } from 'mobx';
import { AssessmentItem } from '@shared/models/assesmentitem';

@injectable()
export default class FamiliesStore extends BaseStore<ClientItem, FamiliesService> {
  static diToken = Symbol('families-store');

  private _clientPrimaryAccountFirstName = '';
  private _clientPrimaryAccountLastName = '';
  private _timezones: ITimezone[] = [];

  constructor() {
    super({ service: container.get<FamiliesService>(FamiliesService.diToken) });
    makeObservable<any>(this, {
      _clientPrimaryAccountFirstName: observable,
      _clientPrimaryAccountLastName: observable,
      _timezones: observable,
      getFamilyInfo: action,
      getList: action,
      getAssessmentsList: action,
      getAssessmentsHistory: action,
      getDraftAssessment: action,
    });

    this.loading = {
      list: false,
      item: false,
    };
  }

  get clientPrimaryAccountFirstName() {
    return this._clientPrimaryAccountFirstName;
  }

  get clientPrimaryAccountLastName() {
    return this._clientPrimaryAccountLastName;
  }

  get timezones() {
    return this._timezones;
  }

  getFamilyInfo = async (id: number) => {
    const familyData = await this.service.getFamilyInfo(id);

    runInAction(() => {
      this._clientPrimaryAccountFirstName = familyData.firstName;
      this._clientPrimaryAccountFirstName = familyData.lastName;
    });

    return familyData;
  };

  getList = async (params?: GetFamiliesParams) => {
    runInAction(() => {
      this.loading.list = true;
    });

    try {
      const { items, pageInfo } = await this.service.getFamiliesList(params);

      runInAction(() => {
        this._list = items;
        this._pageInfo = pageInfo;
      });

      return {
        items,
        pageInfo,
      };
    } finally {
      runInAction(() => {
        this.loading.list = false;
      });
    }
  };

  getAssessmentsList = async (childId: number) => {
    return await this.service.getAssessmentsList(childId);
  };

  getAssessmentsHistory = async (childId: number, page: number, pageSize: number) => {
    return await this.service.getAssessmentsHistory(childId, page, pageSize);
  };

  getDraftAssessment = childId => {
    return this.service.getDraftAssessment(childId);
  };

  getInReviewAssessment = (childId: number) => {
    return this.service.getInReviewAssessment(childId);
  };

  addExtraAssessment = async (clientPrimaryAccountId: number) => {
    return await this.service.addExtraAssessment(clientPrimaryAccountId);
  };

  getSubmittedAssessment = async (
    childId: number,
    assessmentId: number,
  ): Promise<AssessmentItem | undefined> => {
    return await this.service.getSubmittedAssessment(childId, assessmentId);
  };

  getDraftAssessmentDetails = async (childId: number, assessmentId: number) => {
    return await this.service.getDraftAssessmentDetails(childId, assessmentId);
  };

  getTreatmentPlan = async (planId: number) => {
    return await this.service.getTreatmentPlan(planId);
  };

  getCurrentTreatmentPlan = async (childId: number) => {
    return await this.service.getCurrentTreatmentPlan(childId);
  };

  getOptionsProgress = async (childId: number, treatmentOptionId: number) => {
    return await this.service.getOptionsProgress(childId, treatmentOptionId);
  };

  getQualityReport = async (childId: number, id: number) => {
    return await this.service.getQualityReport(childId, id);
  };

  current = async () => {
    return await this.service.current();
  };

  assignClinician = async (params: { clinicianSsoId: string; childId: number }) => {
    return this.service.assignClinician(params);
  };

  unassignClinician = async (childId: number) => {
    return this.service.unassignClinician(childId);
  };

  callCompletedPlanReview = async (childId: number) => {
    return this.service.callCompletedPlanReview(childId);
  };

  addFamily = async (data: IAddFamilyData) => {
    return await this.service.addFamily(data);
  };

  editFamily = async (id: number, data: IEditFamilyData) => {
    return await this.service.editFamily(id, data);
  };

  deleteFamily = async (id: number) => {
    return await this.service.deleteFamily(id);
  };

  checkFamilyUniqueEmail = async (email: string, clientPrimaryAccountId?: number) => {
    return await this.service.checkFamilyUniqueEmail(email, clientPrimaryAccountId);
  };

  getTimezones = async () => {
    return await this.service.getTimezonesList();
  };

  addTcm = async (data: IAddTcmData) => {
    return await this.service.addTcm(data);
  };

  editTcm = async (id: number, data: IAddTcmData) => {
    return await this.service.editTcm(id, data);
  };

  deleteTcm = async (id: number, childId?: number) => {
    return await this.service.deleteTcm(id, childId);
  };

  deleteTcmChild = async (id: number, childId: number) => {
    return await this.service.deleteTcmChild(id, childId);
  };

  addTcmChild = async (id: number, childId: number, familyRole: FamilyRelationship | string) => {
    return await this.service.addTcmChild(id, childId, familyRole);
  };

  deleteChild = async (id: number) => {
    return await this.service.deleteChild(id);
  };

  getClientOptions = async () => {
    return this.service.getClientOptions();
  };

  getTcmChildAvailableOptions = async (childId: number) => {
    return this.service.getTcmChildAvailableOptions(childId);
  };
}
